import React from 'react';
import './CreateJobAdvanced.scss';
import { createJobConfigHooks } from './hooks';
import {
  Card,
  CardHeader,
  CardContent,
  TextField,
  Grid,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
} from '@mui/material';
import { JobDetailsCard } from './components/JobDetailsCard';
import { NotificationsCard } from './components/NotificationsCard';
import { ScheduleCard } from './components/ScheduleCard';

// Not shown in the UI: clients needing a different matching algorithm
// (updateFirst / updateAll / recreateAll2) use the basic/JSON editor instead.
// Mirrors `defaultMatchAlgorithm` in charlotte-jobs/src/mep-import/matchDeductions/index.ts.
const DEFAULT_MATCHING_ALGORITHM = { type: 'recreateAll3', useEffectiveDateAsBeginDate: false };

const DEFAULT_CONFIG = {
  kronos: {
    type: '1password_item',
    '1password_item_path': 'Client credentials/UKG - EnjoyWurkAdmin - apiuser',
  },
  sftp: {
    type: '1password_item',
    '1password_item_path': 'Client credentials/SFTP PCS',
  },
  emailConfig: {
    emailContacts: [],
    sendSuccessEmails: 'true',
  },
  reportDetails: {
    reportID: '',
  },
  itc: '',
  matchFiles: '.*',
  searchCriteriaToDownload: { company: '' },
  // searchCriteriaToUpload is intentionally absent by default: the job runner
  // auto-resolves a company ID (getCompanyId) when it's not set, so we only
  // write it once a value is actually filled in.
  sourceDirectory: '/migrated/wurk/OUT',
  processedDirectory: '/migrated/wurk/Archive',
  mapping: {
    '401kDollar': '',
    '401kPercent': '',
    RothDollar: '',
    RothPercent: '',
    earningListName: '',
  },
  matchingAlgorithm: DEFAULT_MATCHING_ALGORITHM,
  // loanImport is intentionally absent by default: LoanImportCard only writes
  // it once it's actually enabled or has a deduction name filled in.
};

const ClientPlanCard = ({ useJobConfig }) => {
  const [companyName, setCompanyName] = useJobConfig(['companyName']);
  const [itc, setItc] = useJobConfig(['itc']);
  const [reportID, setReportID] = useJobConfig(['reportDetails', 'reportID']);
  return (
    <Card>
      <CardHeader title="Client & Plan" subheader="Identifies which client this feed is for" />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Company short name"
              variant="standard"
              helperText="Internal client identifier"
              required
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="ITC number"
              variant="standard"
              helperText="e.g. ITC001"
              required
              value={itc}
              onChange={(e) => setItc(e.target.value)}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Kronos report ID"
              variant="standard"
              helperText="Saved report ID with the current deductions"
              required
              value={reportID}
              onChange={(e) => setReportID(e.target.value)}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const SearchCriteriaCard = ({ useJobConfig, useJobConfigPatch }) => {
  const [downloadId, setDownloadId] = useJobConfig(['searchCriteriaToDownload', 'company']);
  const [config, patchConfig] = useJobConfigPatch();
  const uploadId = config.searchCriteriaToUpload?.company_id || '';
  const setUploadId = (value) => {
    // Omit the section entirely when blank rather than persisting company_id: '' —
    // the job runner auto-resolves a company ID when this is missing.
    patchConfig({ searchCriteriaToUpload: value ? { company_id: value } : undefined });
  };
  return (
    <Card>
      <CardHeader title="Kronos search criteria" subheader="The UKG company ID used to find the client's records" />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="UKG company short name (download)"
              variant="standard"
              helperText="Used when fetching the current deductions report"
              required
              value={downloadId || ''}
              onChange={(e) => setDownloadId(e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="UKG company ID (upload)"
              variant="standard"
              helperText="Used when uploading the updated deductions. Leave blank to auto-resolve from the company name."
              value={uploadId || ''}
              onChange={(e) => setUploadId(e.target.value)}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const DeductionMappingCard = ({ useJobConfig }) => {
  const [dollar401k, setDollar401k] = useJobConfig(['mapping', '401kDollar']);
  const [percent401k, setPercent401k] = useJobConfig(['mapping', '401kPercent']);
  const [rothDollar, setRothDollar] = useJobConfig(['mapping', 'RothDollar']);
  const [rothPercent, setRothPercent] = useJobConfig(['mapping', 'RothPercent']);
  const [earningsList, setEarningsList] = useJobConfig(['mapping', 'earningListName']);
  return (
    <Card>
      <CardHeader title="Deduction mapping" subheader="Kronos deduction names for each contribution type" />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField fullWidth label="401(k) dollar deduction name" variant="standard" required value={dollar401k} onChange={(e) => setDollar401k(e.target.value)} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label="401(k) percent deduction name" variant="standard" required value={percent401k} onChange={(e) => setPercent401k(e.target.value)} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label="Roth dollar deduction name" variant="standard" required value={rothDollar} onChange={(e) => setRothDollar(e.target.value)} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label="Roth percent deduction name" variant="standard" required value={rothPercent} onChange={(e) => setRothPercent(e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Earnings list name" variant="standard" required value={earningsList} onChange={(e) => setEarningsList(e.target.value)} />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// The job runner reads loanImport.enabled and loanImport.loanDeductionName
// unconditionally, so both must always be written together, even when
// disabled/blank — never just the single field the user touched.
const LoanImportCard = ({ useJobConfigPatch }) => {
  const [config, patchConfig] = useJobConfigPatch();
  const loanImport = config.loanImport || {};
  const enabled = !!loanImport.enabled;
  const patchLoanImport = (fields) => {
    const merged = { enabled: false, loanDeductionName: '', loan2DeductionName: '', loan3DeductionName: '', ...loanImport, ...fields };
    const isUntouched = !merged.enabled && !merged.loanDeductionName && !merged.loan2DeductionName && !merged.loan3DeductionName;
    // Not enabled and nothing filled in — drop the section entirely rather
    // than persisting an all-defaults object. JSON.stringify omits undefined.
    patchConfig({ loanImport: isUntouched ? undefined : merged });
  };
  return (
    <Card>
      <CardHeader title="Loan import" subheader="Import 401(k) loan changes in addition to deductions" />
      <CardContent>
        <FormGroup>
          <FormControlLabel
            control={<Checkbox />}
            checked={enabled}
            onChange={(e, checked) => patchLoanImport({ enabled: checked })}
            label="Enable loan importing"
          />
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField fullWidth label="Loan 1 deduction name" variant="standard" disabled={!enabled} required={enabled} value={loanImport.loanDeductionName || ''} onChange={(e) => patchLoanImport({ loanDeductionName: e.target.value })} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="Loan 2 deduction name" variant="standard" disabled={!enabled} value={loanImport.loan2DeductionName || ''} onChange={(e) => patchLoanImport({ loan2DeductionName: e.target.value })} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="Loan 3 deduction name" variant="standard" disabled={!enabled} value={loanImport.loan3DeductionName || ''} onChange={(e) => patchLoanImport({ loan3DeductionName: e.target.value })} />
            </Grid>
          </Grid>
        </FormGroup>
      </CardContent>
    </Card>
  );
};

const DirectoriesCard = ({ useJobConfig }) => {
  const [sourceDirectory] = useJobConfig(['sourceDirectory']);
  const [processedDirectory] = useJobConfig(['processedDirectory']);
  return (
    <Card>
      <CardHeader title="SFTP Directories" subheader="Set automatically, shown here for reference only." />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField fullWidth label="Source directory" variant="standard" helperText="The job reads incoming files from this folder" value={sourceDirectory} disabled onChange={() => {}} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label="Processed directory" variant="standard" helperText="The job moves files here after processing" value={processedDirectory} disabled onChange={() => {}} />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const AdvancedCard = ({ useJobConfig }) => {
  const [matchFiles, setMatchFiles] = useJobConfig(['matchFiles']);
  const [sendAttachment, setSendAttachment] = useJobConfig(['sendAttachment']);
  const [debug, setDebug] = useJobConfig(['debug']);
  const [useEinTaxId, setUseEinTaxId] = useJobConfig(['useEinTaxIdInsteadOfEinName']);
  const [delayedCheck, setDelayedCheck] = useJobConfig(['delayedCheck']);
  const [ignoreLoginAsError, setIgnoreLoginAsError] = useJobConfig(['ignoreLoginAsError']);
  const [loginAsUsername, setLoginAsUsername] = useJobConfig(['loginAsUsername']);

  return (
    <Card>
      <CardHeader title="Advanced / Experimental" subheader="Rarely needed" />
      <CardContent>
        <FormGroup>
          <TextField
            fullWidth
            label="File match pattern"
            variant="standard"
            helperText="Regular expression incoming files must match. Defaults to '.*' (match all)."
            value={matchFiles || '.*'}
            onChange={(e) => setMatchFiles(e.target.value)}
          />
          <TextField
            fullWidth
            label="Login as username"
            variant="standard"
            helperText="Only needed if this client requires Kronos impersonation of a specific user"
            value={loginAsUsername || ''}
            onChange={(e) => setLoginAsUsername(e.target.value)}
            sx={{ marginTop: '16px' }}
          />
          <FormControlLabel
            control={<Checkbox />}
            checked={!!ignoreLoginAsError}
            onChange={(e, checked) => setIgnoreLoginAsError(checked)}
            label={<>Ignore impersonation errors<FormHelperText>If logging in as another username, ignore errors if they happen</FormHelperText></>}
          />
          <FormControlLabel
            control={<Checkbox />}
            checked={sendAttachment === true}
            onChange={(e, checked) => setSendAttachment(checked)}
            label={<>Send results as an attachment<FormHelperText>Attach an Excel file with the results instead of a plain message</FormHelperText></>}
          />
          <FormControlLabel
            control={<Checkbox />}
            checked={!!useEinTaxId}
            onChange={(e, checked) => setUseEinTaxId(checked)}
            label={<>[Experimental] Use EIN tax ID instead of EIN name</>}
          />
          <FormControlLabel
            control={<Checkbox />}
            checked={!!delayedCheck}
            onChange={(e, checked) => setDelayedCheck(checked)}
            label={<>[Experimental] Upload everything first, then check all uploads</>}
          />
          <FormControlLabel
            control={<Checkbox />}
            checked={!!debug}
            onChange={(e, checked) => setDebug(checked)}
            label="Output extra debug details"
          />
        </FormGroup>
      </CardContent>
    </Card>
  );
};

const ImportFormBody = ({ partner, defaultConfig }) => {
  const { useJobGeneralConfig, useJobConfig, useJobConfigPatch } = createJobConfigHooks({ partner, defaultConfig });
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <JobDetailsCard useJobGeneralConfig={useJobGeneralConfig} />
      </Grid>
      <Grid item xs={12}>
        <ClientPlanCard useJobConfig={useJobConfig} />
      </Grid>
      <Grid item xs={12}>
        <SearchCriteriaCard useJobConfig={useJobConfig} useJobConfigPatch={useJobConfigPatch} />
      </Grid>
      <Grid item xs={12}>
        <DeductionMappingCard useJobConfig={useJobConfig} />
      </Grid>
      <Grid item xs={12}>
        <LoanImportCard useJobConfigPatch={useJobConfigPatch} />
      </Grid>
      <Grid item xs={12}>
        <DirectoriesCard useJobConfig={useJobConfig} />
      </Grid>
      <Grid item xs={12}>
        <NotificationsCard useJobConfig={useJobConfig} />
      </Grid>
      <Grid item xs={12}>
        <ScheduleCard useJobGeneralConfig={useJobGeneralConfig} />
      </Grid>
      <Grid item xs={12}>
        <AdvancedCard useJobConfig={useJobConfig} />
      </Grid>
    </Grid>
  );
};

export const MepImportForm = () => <ImportFormBody partner="PCS" defaultConfig={DEFAULT_CONFIG} />;
export const PcsImportForm = () => <ImportFormBody partner="PCS" defaultConfig={DEFAULT_CONFIG} />;
